import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Typography,
  Paper,
} from '@mui/material';
import ListIcon from '@mui/icons-material/List';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PeopleIcon from '@mui/icons-material/People';
import { COLORS } from '../../constants/radar';
import { employeesApi } from '../../services/api';
import { getDurationFromNow } from '../../utils/dateUtils';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { EmployeeAvatar } from '../../components/common/EmployeeAvatar';
import { useAlert } from '../../contexts/AlertContext';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getStatusColor, 
  getStatusTextColor, 
  getStatusBorderColor,
  getStatusText,
  getReviewerStatus,
} from '../../constants/assignmentStatuses';
import {
  getFeedbackStatusColor,
  getFeedbackStatusTextColor,
  getFeedbackStatusBorderColor,
  getFeedbackStatusText,
  determineFeedbackStatus
} from '../../constants/feedbackOnDemandStatuses';
import { FEEDBACK_ON_DEMAND_TYPES } from '../../constants/feedbackOnDemandTypes';
import {
  FEEDBACK_SESSION_STATUSES,
  getStatusText as getSessionStatusText,
  getStatusColor as getSessionStatusColor,
  getStatusTextColor as getSessionStatusTextColor,
  getStatusBorderColor as getSessionStatusBorderColor
} from '../../constants/feedbackSessionStatuses';
import {
  REVIEW_TYPES,
  getReviewTypeColor,
  getReviewTypeTextColor,
  getReviewTypeText
} from '../../constants/reviewTypes';
import { getTemplateTypeText } from '../../constants/matrixTemplateTypes';
import { ReviewTypeBadge } from '../../components/common/ReviewTypeBadge';

const ActivitiesCard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState(null);
  const [totalActivities, setTotalActivities] = useState(0);
  const { showAlert } = useAlert();

  const handleAssignmentClick = (item) => {
    navigate(`/skills-matrix/assignments/${item.assignment.id}/${user.employee.id}`);
  };

  // Fetch notifications data
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        const data = await employeesApi.getNotifications();
        setNotifications(data);
      } catch (err) {
        console.error('Error fetching notifications:', err);

        showAlert('Failed to load activities', 'error', err);

        // Still set notifications to empty object to avoid null checks
        setNotifications({
          assignments: { count: 0, items: [] },
          feedback: { count: 0, items: [] }
        });
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [showAlert]);

  // Calculate total activities whenever notifications change
  useEffect(() => {
    if (notifications) {
      const assignmentsCount = notifications.assignments?.count || 0;
      const feedbackCount = notifications.feedback?.count || 0;
      const configureCount = notifications.configureFeedback?.count || 0;
      setTotalActivities(assignmentsCount + feedbackCount + configureCount);
    }
  }, [notifications]);


  // Render loading state
  if (loading) {
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: COLORS.BACKGROUND_LIGHT,
          width: 400,
          height: 240,
          mt: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <LoadingSpinner message="Loading activities..." />
      </Card>
    );
  }

  const hasAssignments = notifications?.assignments?.count > 0;
  const hasFeedback = notifications?.feedback?.count > 0;
  const hasConfigureFeedback = notifications?.configureFeedback?.count > 0;

  // Render activities
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: COLORS.BACKGROUND_LIGHT,
        width: 400,
        mt: 1,
        overflow: 'hidden'
      }}
    >
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ 
          p: 2, 
          backgroundColor: COLORS.PRIMARY,
          color: 'white'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Your Activities
          </Typography>
          <Typography variant="body2">
            You have {totalActivities} pending activities
          </Typography>
        </Box>

        {totalActivities === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            p: 4,
            height: 150
          }}>
            <Typography variant="body1" color="text.secondary">
              No pending activities
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              You're all caught up!
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 400, overflow: 'auto', py: 0 }}>
            {/* Assignments section */}
            {hasAssignments && (
              <>
                <Box sx={{ py: 1.5, px: 2, backgroundColor: 'rgba(0,0,0,0.03)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ListIcon fontSize="small" sx={{ color: COLORS.SECONDARY }} />
                    <Typography variant="subtitle2" sx={{ color: COLORS.PRIMARY }}>
                      Assignments
                    </Typography>
                  </Box>
                </Box>
                
                {/* All assignments */}
                <Box sx={{ p: 0 }}>
                  {notifications.assignments.items.map((item, index) => {
                    const status = getReviewerStatus(item);
                    const reviewType = item.reviewType || REVIEW_TYPES.SEQUENTIAL; // Default to sequential if not specified
                    const isIndividualAssignment = item.isIndividualAssignment;
                    return (
                      <Paper 
                        key={item.id || index}
                        elevation={0} 
                        sx={{ 
                          m: 1, 
                          p: 1.5, 
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
                          <EmployeeAvatar employee={item.for} size={36} sx={{ mr: 1 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                            Feedback for
                            </Typography>
                            <Typography 
                              variant="body1" 
                              fontWeight={500} 
                              color={COLORS.PRIMARY}
                              sx={{ flex: 1 }}
                            >
                              {item.for?.fullName || "Assignment"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.templateType ? `${getTemplateTypeText(item.templateType)}` : ''}
                              {item.order === 1 ? ' • Self Assessment' : item.order ? ` • Reviewer ${item.order}` : ''}
                            </Typography>
                          </Box>
                          <Chip
                            label={getStatusText(status)}
                            variant="outlined"
                            onClick={() => handleAssignmentClick(item)}
                            sx={{
                              backgroundColor: getStatusColor(status),
                              color: getStatusTextColor(status),
                              borderColor: getStatusBorderColor(status),
                              borderWidth: 1,
                              fontWeight: 500,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                filter: 'brightness(0.95)',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              },
                            }}
                          />
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <ReviewTypeBadge 
                            reviewType={reviewType}
                            getReviewTypeColor={getReviewTypeColor}
                            getReviewTypeTextColor={getReviewTypeTextColor}
                            getReviewTypeText={getReviewTypeText}
                          />
                            <>
                              <Typography variant="caption" sx={{ color: '#64748B' }}>•</Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: '#64748B',
                                  fontWeight: 500
                                }}
                              >
                                {isIndividualAssignment ? 'Individual Assignment' : item.feedbackSessionName}
                              </Typography>
                            </>
                        </Box>
                        
                        {item.deadline && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#64748B',
                              fontStyle: 'italic',
                              display: 'block'
                            }}
                          >
                            Session due in {getDurationFromNow(item.deadline)}
                          </Typography>
                        )}
                      </Paper>
                    );
                  })}
                </Box>
              </>
            )}

            {/* Configure Feedback section */}
            {hasConfigureFeedback && (
              <>
                {hasAssignments && <Divider />}
                
                <Box sx={{ py: 1.5, px: 2, backgroundColor: 'rgba(0,0,0,0.03)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PeopleIcon fontSize="small" sx={{ color: COLORS.SECONDARY }} />
                    <Typography variant="subtitle2" sx={{ color: COLORS.PRIMARY }}>
                      Configure Feedback Evaluators
                    </Typography>
                  </Box>
                </Box>
                
                {/* Configure Feedback items */}
                <Box sx={{ p: 0 }}>
                  {notifications.configureFeedback.items.map((item, index) => (
                    <Paper 
                      key={item.id || index}
                      elevation={0} 
                      sx={{ 
                        m: 1, 
                        p: 1.5, 
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography 
                            variant="body1" 
                            fontWeight={500} 
                            color={COLORS.PRIMARY}
                            sx={{ lineHeight: 1.2 }}
                          >
                            {item.feedbackSession.name}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#64748B',
                              display: 'block',
                              lineHeight: 1.2
                            }}
                          >
                            {item.skillsMatrix.name}
                          </Typography>
                        </Box>
                          <Chip 
                            label={getSessionStatusText(FEEDBACK_SESSION_STATUSES.CONFIGURATION)}
                            variant="outlined"
                            size="small" 
                            sx={{ 
                              backgroundColor: getSessionStatusColor(FEEDBACK_SESSION_STATUSES.CONFIGURATION),
                              color: getSessionStatusTextColor(FEEDBACK_SESSION_STATUSES.CONFIGURATION),
                              borderColor: getSessionStatusBorderColor(FEEDBACK_SESSION_STATUSES.CONFIGURATION),
                              borderWidth: 1,
                              fontWeight: 500,
                              fontSize: '0.75rem',
                              py: 0.25,
                              px: 1,
                              ml: 1,
                              height: 36
                            }} 
                          />
                      </Box>
                      {item.feedbackSession.deadlineAt && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: '#64748B',
                            fontStyle: 'italic',
                            display: 'block'
                          }}
                        >
                          Session due in {getDurationFromNow(item.feedbackSession.deadlineAt)}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Box>
              </>
            )}

            {/* Feedback section */}
            {hasFeedback && (
              <>
                {(hasAssignments || hasConfigureFeedback) && <Divider />}
                
                <Box sx={{ py: 1.5, px: 2, backgroundColor: 'rgba(0,0,0,0.03)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ChatBubbleOutlineIcon fontSize="small" sx={{ color: COLORS.SECONDARY }} />
                    <Typography variant="subtitle2" sx={{ color: COLORS.PRIMARY }}>
                      Instant Feedbacks
                    </Typography>
                  </Box>
                </Box>
                
                {/* Feedback items */}
                <Box sx={{ p: 0 }}>
                  {notifications.feedback.items.map((item, index) => {
                    const feedbackStatus = determineFeedbackStatus(item);
                    const isGiving = item.type === FEEDBACK_ON_DEMAND_TYPES.GIVING;
                    const avatarEmployee = isGiving ? item.receiver : item.sender;
                    
                    return (
                      <Paper 
                        key={item.id || index}
                        elevation={0} 
                        sx={{ 
                          m: 1, 
                          p: 1.5, 
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          <EmployeeAvatar employee={avatarEmployee} size={36} sx={{ mr: 1 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography 
                              variant="body1" 
                              fontWeight={500} 
                              color={COLORS.PRIMARY}
                              sx={{ lineHeight: 1.2 }}
                            >
                              {isGiving ? "Feedback to provide" : "Feedback request"}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#64748B',
                                display: 'block',
                                lineHeight: 1.2
                              }}
                            >
                              {isGiving 
                                ? `For: ${item.receiver?.fullName || 'Unknown'}`
                                : `From: ${item.sender?.fullName || 'Unknown'}`
                              }
                            </Typography>
                          </Box>
                          <Chip 
                            label={getFeedbackStatusText(feedbackStatus)}
                            variant="outlined"
                            size="small" 
                            sx={{ 
                              backgroundColor: getFeedbackStatusColor(feedbackStatus),
                              color: getFeedbackStatusTextColor(feedbackStatus),
                              borderColor: getFeedbackStatusBorderColor(feedbackStatus),
                              borderWidth: 1,
                              fontWeight: 500,
                              fontSize: '0.75rem',
                              py: 0.25,
                              px: 1,
                              ml: 1,
                              height: 36
                            }} 
                          />
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              </>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivitiesCard; 